import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { auth } from '../firebase';
import EmailService from '../services/EmailService';

const EmailContext = createContext();

export const useEmail = () => {
  const context = useContext(EmailContext);
  if (!context) {
    throw new Error('useEmail must be used within EmailProvider');
  }
  return context;
};

export const EmailProvider = ({ children }) => {
  const [userId, setUserId] = useState(null);
  const [isGmailAuthorized, setIsGmailAuthorized] = useState(false);
  const [activeAccountId, setActiveAccountId] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [emails, setEmails] = useState([]);
  const [nextPageToken, setNextPageToken] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [view, setView] = useState('FOCUSED');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isAddingAccount, setIsAddingAccount] = useState(false);
  const [isDeletingAccount, setIsDeletingAccount] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredEmails, setFilteredEmails] = useState([]);
  const [showSearch, setShowSearch] = useState(false);
  const [searchInputValue, setSearchInputValue] = useState('');
  const [composeData, setComposeData] = useState(null);
  const [showComposeModal, setShowComposeModal] = useState(false);

  // Bulk selection state
  const [selectedEmailIds, setSelectedEmailIds] = useState(new Set());

  // Toggle email selection
  const toggleEmailSelection = useCallback((emailId) => {
    setSelectedEmailIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(emailId)) {
        newSet.delete(emailId);
      } else {
        newSet.add(emailId);
      }
      return newSet;
    });
  }, []);

  // Select all visible emails
  const selectAllEmails = useCallback(() => {
    const visibleEmails = searchQuery ? filteredEmails : emails;
    setSelectedEmailIds(new Set(visibleEmails.map((e) => e.id)));
  }, [emails, filteredEmails, searchQuery]);

  // Clear all selections
  const clearEmailSelection = useCallback(() => {
    setSelectedEmailIds(new Set());
  }, []);

  // Get selected emails as array
  const getSelectedEmails = useCallback(() => {
    const visibleEmails = searchQuery ? filteredEmails : emails;
    return visibleEmails.filter((e) => selectedEmailIds.has(e.id));
  }, [emails, filteredEmails, searchQuery, selectedEmailIds]);

  const fetchAccounts = useCallback(async (uid) => {
    const userIdToUse = uid || userId;
    if (!userIdToUse) return;
    
    try {
      const data = await EmailService.getAccounts(userIdToUse);
      const accs = data.accounts || [];
      setAccounts(accs);
      
      // Only set activeAccountId if it's not already set and we have accounts
      if (accs.length > 0 && !activeAccountId) {
        setActiveAccountId(accs[0].id);
      }
    } catch (error) {
      console.error('Error fetching accounts:', error);
    }
  }, [userId, activeAccountId]);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      if (user) {
        setUserId(user.uid);
        let status = { authorized: false, email: null };
        try {
          status = await EmailService.checkGmailAuth(user.uid);
        } catch (error) {
          console.warn('Gmail auth check failed (backend unreachable):', error);
        }
        setIsGmailAuthorized(status.authorized);
        if (status.authorized) {
          // Call the function directly, not through the reference
          EmailService.getAccounts(user.uid).then(data => {
            const accs = data.accounts || [];
            setAccounts(accs);
            if (accs.length > 0) {
              setActiveAccountId(accs[0].id);
            }
          }).catch(error => {
            console.error('Error fetching accounts:', error);
          });
        }
      } else {
        setUserId(null);
        setIsGmailAuthorized(false);
        setActiveAccountId(null);
        setEmails([]);
      }
    });
    return () => unsubscribe();
  }, []); // No dependencies - this only runs once

  const handleGmailAuth = useCallback(async () => {
    if (!userId) {
      console.error('No user logged in');
      setError('You must be logged in to connect your Gmail account');
      return;
    }
    
    setIsAddingAccount(true);
    setError(null);
    
    try {
      console.log('Initiating Gmail authentication');
      await EmailService.initiateGmailAuth(userId);
    } catch (error) {
      console.error('Failed to initiate Gmail auth:', error);
      setError('Failed to connect Gmail account. Please try again.');
    } finally {
      setIsAddingAccount(false);
    }
  }, [userId]);

  const handleGmailCallback = useCallback(async (code) => {
    if (!userId) {
      console.error('No user logged in');
      return { success: false, error: 'You must be logged in to connect your Gmail account' };
    }

    setIsAddingAccount(true);
    setError(null);

    try {
      console.log('Processing Gmail callback with code');
      const response = await EmailService.authorizeGmail(userId, code);
      console.log('Gmail authorization response:', response);

      if (response.success) {
        setIsGmailAuthorized(true);
        
        // Always update accounts list with the latest data
        if (response.data?.accounts) {
          setAccounts(response.data.accounts);
          
          // If this is the first account, set it as active
          if (response.data.accounts.length > 0 && !activeAccountId) {
            setActiveAccountId(response.data.accounts[0].id);
          }
          
          // If a new account was added, try to set it as active
          if (response.accountAdded && response.email) {
            const newAccount = response.data.accounts.find(acc => acc.email === response.email);
            if (newAccount) {
              setActiveAccountId(newAccount.id);
            }
          }
        }

        // Return appropriate response based on whether account was added
        if (response.accountAdded) {
          return { 
            success: true, 
            accountAdded: true,
            email: response.email 
          };
        } else {
          return { 
            success: true, 
            accountAdded: false, 
            error: response.error || 'This Gmail account is already connected.' 
          };
        }
      } else {
        const errorMsg = response.error || 'Failed to connect Gmail account';
        setError(errorMsg);
        return { success: false, error: errorMsg };
      }
    } catch (error) {
      console.error('Error in Gmail callback:', error);
      const errorMsg = error.message || 'An unexpected error occurred';
      setError(errorMsg);
      return { success: false, error: errorMsg };
    } finally {
      setIsAddingAccount(false);
    }
  }, [userId, activeAccountId]);

  const handleSwitchAccount = useCallback((accountId, onSwitchCallback) => {
    if (accountId && accountId !== activeAccountId) {
      setActiveAccountId(accountId);
      // Clear emails when switching accounts to avoid stale data
      setEmails([]);
      // Reset pagination
      setNextPageToken(null);
      setHasMore(false);
      
      // If a callback was provided, execute it
      if (typeof onSwitchCallback === 'function') {
        onSwitchCallback();
      }
      // Emails will be fetched automatically by the useEffect below
      // when activeAccountId changes
    }
  }, [activeAccountId]);

  const fetchEmails = useCallback(async (reset = false) => {
    if (!userId || !activeAccountId || !isGmailAuthorized) return;
    
    // Prevent multiple simultaneous fetches
    if (loading) return;
    
    setLoading(true);
    setError(null);
    
    try {
      if (reset) {
        setEmails([]);
        setNextPageToken(null);
        setHasMore(false);
      }
      
      const options = {
        pageToken: reset ? null : nextPageToken,
        view,
        limit: 10
      };
      
      // Add search query if it exists (make it case insensitive)
      if (searchQuery) {
        options.q = searchQuery.toLowerCase();
        console.log('Adding case-insensitive search query:', options.q);
      }
      
      console.log('Fetching emails with options:', options);
      const result = await EmailService.getEmails(userId, activeAccountId, options);
      
      if (result.error) {
        setError(result.error);
        console.error('Error fetching emails:', result.error);
        return;
      }
      
      const newEmails = result.emails || [];
      console.log('API returned emails:', newEmails.length);
      
      // Handle search results vs regular email fetching
      if (searchQuery) {
        console.log(`Search returned ${newEmails.length} results for query: "${searchQuery}"`);
        setFilteredEmails(newEmails);
        
        // Only update main emails array during reset to avoid unnecessary re-renders
        if (reset) {
          setEmails(newEmails);
        }
      } else {
        // Not searching, update the main emails array
        setEmails(prev => reset ? newEmails : [...prev, ...newEmails]);
        
        // Make sure filtered emails is empty when not searching
        if (filteredEmails.length > 0) {
          setFilteredEmails([]);
        }
      }
      
      // Only update these if they actually changed
      if (result.nextPageToken !== nextPageToken) {
        setNextPageToken(result.nextPageToken || null);
      }
      
      const newHasMore = !!result.nextPageToken;
      if (hasMore !== newHasMore) {
        setHasMore(newHasMore);
      }
    } catch (err) {
      console.error('Unexpected error in fetchEmails:', err);
      setError(err.message || 'An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  }, [userId, activeAccountId, isGmailAuthorized, nextPageToken, view, searchQuery, loading, hasMore, filteredEmails.length]);

  // Load emails when account changes or view changes
  useEffect(() => {
    if (userId && activeAccountId && isGmailAuthorized) {
      console.log('Loading emails for account and view:', {
        userId, 
        accountId: activeAccountId, 
        view
      });
      // Reset query when changing accounts
      setSearchQuery('');
      
      // Call fetchEmails inline to avoid circular dependency
      (async () => {
        if (!userId || !activeAccountId || !isGmailAuthorized) return;
        
        setLoading(true);
        setError(null);
        
        try {
          setEmails([]);
          setNextPageToken(null);
          setHasMore(false);
          
          const options = {
            pageToken: null,
            view,
            limit: 10
          };
          
          console.log('Fetching emails with options:', options);
          const result = await EmailService.getEmails(userId, activeAccountId, options);
          
          if (result.error) {
            setError(result.error);
            console.error('Error fetching emails:', result.error);
            return;
          }
          
          const newEmails = result.emails || [];
          console.log('API returned emails:', newEmails.length);
          
          setEmails(newEmails);
          
          // Only update these if they actually changed
          if (result.nextPageToken) {
            setNextPageToken(result.nextPageToken);
            setHasMore(true);
          } else {
            setHasMore(false);
          }
        } catch (err) {
          console.error('Unexpected error loading emails:', err);
          setError(err.message || 'An unexpected error occurred');
        } finally {
          setLoading(false);
        }
      })();
    }
  }, [userId, activeAccountId, isGmailAuthorized, view]);

  const handleLoadMore = useCallback(async () => {
    if (!loading && hasMore) {
      await fetchEmails(false);
    }
  }, [loading, hasMore, fetchEmails]);

  // Get the active email address from the active account
  const activeEmail = accounts.find(account => account.id === activeAccountId)?.email || '';

  const handleDeleteAccount = useCallback(async (accountId) => {
    if (!userId || !accountId) return;
    
    try {
      setIsDeletingAccount(true);
      setError(null);
      
      await EmailService.disconnectAccount(userId, accountId);
      
      setAccounts(prevAccounts => prevAccounts.filter(account => account.id !== accountId));
      
      if (accountId === activeAccountId) {
        const remainingAccounts = accounts.filter(account => account.id !== accountId);
        if (remainingAccounts.length > 0) {
          handleSwitchAccount(remainingAccounts[0].id);
        } else {
          setActiveAccountId(null);
          setEmails([]);
        }
      }
      
      // Fetch accounts directly instead of using the fetchAccounts reference
      const data = await EmailService.getAccounts(userId);
      setAccounts(data.accounts || []);
      
      return { success: true };
    } catch (error) {
      console.error('Failed to disconnect account:', error);
      setError('Failed to disconnect account. Please try again.');
      return { success: false, error };
    } finally {
      setIsDeletingAccount(false);
    }
  }, [userId, activeAccountId, accounts, handleSwitchAccount]);

  const emailsWithAccountId = emails.map(email => ({
    ...email,
    accountId: email.accountId || activeAccountId
  }));

  // Initialize the search input value from context
  useEffect(() => {
    setSearchInputValue(searchQuery || '');
    // Make sure search is visible if there's a query
    if (searchQuery && !showSearch) {
      setShowSearch(true);
    }
  }, [searchQuery, showSearch]);

  // Function to open the compose modal with pre-filled data
  const openCompose = useCallback((data) => {
    setComposeData(data);
    setShowComposeModal(true);
  }, []);

  const contextValue = {
    userId,
    accounts,
    activeAccountId,
    activeEmail,
    isGmailAuthorized,
    loading,
    error,
    emails,
    nextPageToken,
    hasMore,
    view,
    setView,
    setActiveAccountId,
    handleGmailAuth,
    handleGmailCallback,
    fetchAccounts,
    fetchEmails,
    handleLoadMore,
    handleSwitchAccount,
    handleDeleteAccount,
    isAddingAccount,
    isDeletingAccount,
    searchQuery,
    setSearchQuery,
    filteredEmails,
    openCompose,
    composeData,
    showComposeModal,
    setShowComposeModal,
    // Bulk selection
    selectedEmailIds,
    toggleEmailSelection,
    selectAllEmails,
    clearEmailSelection,
    getSelectedEmails,
  };

  return <EmailContext.Provider value={contextValue}>{children}</EmailContext.Provider>;
};

export default EmailContext;